/**
 * TeamStrength Hub - Model Management Page
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { modelApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Layers,
  ChevronDown, ChevronRight, Trash2
} from 'lucide-react';

const ModelPage = () => {
  const { currentOrg } = useAuth();
  const fileInputRef = useRef(null);
  
  const [model, setModel] = useState(null);
  const [categories, setCategories] = useState([]);
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [previewData, setPreviewData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchModel = async () => {
    if (!currentOrg) return;
    
    try {
      setLoading(true);
      const response = await modelApi.get(currentOrg.org_id);
      setModel(response.data.model);
      setCategories(response.data.categories || []);
      setTalents(response.data.talents || []);
    } catch (error) {
      toast.error('Failed to load model');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModel();
  }, [currentOrg]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setSelectedFile(file);
    setUploading(true);
    
    try {
      const response = await modelApi.previewCSV(currentOrg.org_id, file);
      setPreviewData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse CSV');
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      await modelApi.confirmCSV(currentOrg.org_id, selectedFile);
      toast.success('Model uploaded successfully!');
      setPreviewData(null);
      setSelectedFile(null);
      fetchModel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload model');
    } finally {
      setUploading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getTalentsByCategory = (categoryId) => {
    return talents.filter(t => t.category_id === categoryId);
  };

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Layers className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Select an organization to manage the model</p>
        </div>
      </div>
    );
  }

  if (currentOrg.role !== 'org_admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Only Org Admins can manage the strength model</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="model-page">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
          Strength Model
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Define the strengths framework for {currentOrg.name}
        </p>
      </div>

      {/* Upload Section */}
      <Card className="animate-fade-in stagger-1">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Upload Model</CardTitle>
          <CardDescription>
            Upload a CSV file with columns: Category, StrengthName, Color (optional), Description (optional)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!previewData ? (
            <div 
              className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center hover:border-violet-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <FileSpreadsheet className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-2">
                {uploading ? 'Processing...' : 'Drop a CSV file here or click to browse'}
              </p>
              <p className="text-xs text-slate-500">
                Supported models: CliftonStrengths, VIA, Strengthmaker, Custom
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-700 dark:text-emerald-400">
                      {selectedFile?.name}
                    </p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-500">
                      {previewData.total_categories} categories, {previewData.total_talents} strengths
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setPreviewData(null);
                    setSelectedFile(null);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Preview */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 dark:bg-slate-800 p-3 border-b border-slate-200 dark:border-slate-700">
                  <p className="font-medium text-sm">Preview</p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {previewData.categories.map((cat, i) => (
                    <div key={i} className="p-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: cat.color || '#7C3AED' }}
                        />
                        <span className="font-medium text-sm">{cat.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {cat.talents?.length || 0} strengths
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setPreviewData(null);
                    setSelectedFile(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={handleConfirmUpload}
                  disabled={uploading}
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? 'Uploading...' : 'Confirm & Save'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Model */}
      {loading ? (
        <div className="h-64 skeleton rounded-xl" />
      ) : model ? (
        <Card className="animate-fade-in stagger-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Current Model</CardTitle>
                <CardDescription>
                  {model.name} • {categories.length} categories • {talents.length} strengths
                </CardDescription>
              </div>
              <Badge variant={model.is_active ? 'default' : 'secondary'}>
                {model.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {categories.map((category) => {
                const catTalents = getTalentsByCategory(category.category_id);
                const isExpanded = expandedCategories[category.category_id];
                
                return (
                  <div key={category.category_id} className="border rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      onClick={() => toggleCategory(category.category_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: category.color || '#7C3AED' }}
                        />
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {category.name}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {catTalents.length}
                        </Badge>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {catTalents.map((talent) => (
                            <div 
                              key={talent.talent_id}
                              className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
                            >
                              <p className="font-medium text-sm text-slate-700 dark:text-slate-300">
                                {talent.name}
                              </p>
                              {talent.description && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                  {talent.description}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="animate-fade-in stagger-2">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Layers className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
              No model defined
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
              Upload a CSV file to define your organization's strength model.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sample CSV format */}
      <Card className="animate-fade-in stagger-3">
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>CSV Format Example</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre>{`Category,StrengthName,Color,Description
Executing,Achiever,#10B981,Drive to accomplish tasks
Executing,Arranger,#10B981,Organize complexity
Influencing,Activator,#7C3AED,Turn thoughts into action
Influencing,Command,#7C3AED,Take charge of situations
Relationship,Developer,#06B6D4,Recognize potential in others
Strategic,Analytical,#F59E0B,Search for reasons and causes`}</pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModelPage;
